import { Component, OnInit, ChangeDetectionStrategy, Input, ViewChildren, QueryList, ElementRef, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { functionAppLink } from '../../interfaces/function-app-link.interface';
import { functionApps } from '../../interfaces/functionApps.interface';
import { Link } from '../../interfaces/link.interface';
import { FunctionAppsService } from '../../services/function-apps.service';
import { filter, map, switchMap } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { format } from 'devextreme/ui/widget/ui.widget';
import { LinkTemplateService } from '../../services/link-template.service';
import { ApplicationService } from '../../services/application.service';
import { LinkTemplate } from '../../interfaces/link-template.interface';
import { Application } from '../../interfaces/application.interface';
import { DetailedGridFormComponent } from './detailed-grid-form/detailed-grid-form.component';
import { FunctionAppsLinkService, functionAppsLinkCreateDto } from '../../services/function-apps-link.service';
import Swal from 'sweetalert2';
import { AsyncPipe, NgIf } from '@angular/common';

@Component({
	selector: 'detailed-grid-link-functionapps',
	templateUrl: './detailed-grid-link-functionapps.component.html',
	styleUrls: ['./detailed-grid-link-functionapps.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, AsyncPipe, DetailedGridFormComponent]
})
export class DetailedGridLinkFunctionappsComponent implements OnInit {
	@ViewChild('source') sourceChild: DetailedGridFormComponent;
	@ViewChild('target') targetChild: DetailedGridFormComponent;
	@ViewChild('global') globalChild: DetailedGridFormComponent;
	@ViewChild('entrypoint') entrypointChild: DetailedGridFormComponent;

	functionApps$: BehaviorSubject<functionApps[]> = new BehaviorSubject<functionApps[]>([]);
	isLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	sourceFunctionApp: functionApps[];

	sourceFormGroup: FormGroup;
	targetFormGroup: FormGroup;
	globalFormGroup: FormGroup;
	entryPointFormGroup: FormGroup;

	sourceFunctionAppList$: BehaviorSubject<functionApps[]> = new BehaviorSubject<functionApps[]>([]);
	targetFunctionAppList$: BehaviorSubject<functionApps[]> = new BehaviorSubject<functionApps[]>([]);
	globalFunctionAppList$: BehaviorSubject<functionApps[]> = new BehaviorSubject<functionApps[]>([]);
	entryPointFunctionAppList$: BehaviorSubject<functionApps[]> = new BehaviorSubject<functionApps[]>([]);

	selectedSourceFunctionApp: number | undefined;
	selectedTargetFunctionApp: number | undefined;
	selectedGlobalFunctionApp: number | undefined;
	selectedEntryPointFunctionApp: number | undefined;

	/**
	 *
	 */
	constructor(private functionAppsService: FunctionAppsService, private functionAppsLinkService: FunctionAppsLinkService) {}

	@Input() link: Link;
	@Input() applications: Application[];
	@Input() linkTemplates: LinkTemplate[];

	linkAppConfiguration$: Observable<any[]> = new Observable<any[]>();
	functionAppLinkInformation: any[] = [];

	ngOnInit(): void {
		const fromApplicationId = this.linkTemplates.find(item => item.id == this.link.fromLinkTemplateId).applicationId;
		const toApplicationId = this.linkTemplates.find(item => item.id == this.link.toLinkTemplateId).applicationId;
		const globalApplicationId = 9999;
		const entrypointApplicationId = 10000;

		this.functionAppsService.GetCustomConfiguration(this.link.id).subscribe(data => {
			this.sourceFunctionAppList$.next(data.sourceFunctionAppList.filter(item => item.isDefault == false));
			this.targetFunctionAppList$.next(data.targetFunctionAppList.filter(item => item.isDefault == false));
			this.globalFunctionAppList$.next(data.globalFunctionAppList.filter(item => item.isDefault == false));
			this.entryPointFunctionAppList$.next(data.entryPointFunctionAppList.filter(item => item.isDefault == false));

			this.selectedSourceFunctionApp = data.sourceFunctionApp?.id;
			this.selectedTargetFunctionApp = data.targetFunctionApp?.id;
			this.selectedGlobalFunctionApp = data.globalFunctionApp?.id;
			this.selectedEntryPointFunctionApp = data.entryPointFunctionApp?.id;

			this.sourceFormGroup = new FormGroup({
				description: new FormControl({ value: this.applications.find(item => item.id == fromApplicationId).name, disabled: true }),
				selectedFunctionAppDescription: new FormControl({ value: data?.sourceFunctionApp?.description, disabled: true }),
				selectedFunctionApp: new FormControl(data?.sourceFunctionApp?.id),
			});
			this.targetFormGroup = new FormGroup({
				description: new FormControl({ value: this.applications.find(item => item.id == toApplicationId).name, disabled: true }),
				selectedFunctionAppDescription: new FormControl({ value: data?.targetFunctionApp?.description, disabled: true }),
				selectedFunctionApp: new FormControl(data?.targetFunctionApp?.id),
			});

			this.entryPointFormGroup = new FormGroup({
				description: new FormControl({ value: this.applications.find(item => item.id == entrypointApplicationId).name, disabled: true }),
				selectedFunctionAppDescription: new FormControl({ value: data?.entryPointFunctionApp?.description, disabled: true }),
				selectedFunctionApp: new FormControl(data?.entryPointFunctionApp?.id),
			});

			this.globalFormGroup = new FormGroup({
				description: new FormControl({
					value: this.applications.find(item => item.id == globalApplicationId).name,
					disabled: true,
				}),
				selectedFunctionAppDescription: new FormControl({ value: data?.globalFunctionApp?.description, disabled: true }),
				selectedFunctionApp: new FormControl(data?.globalFunctionApp?.id),
			});
			//this.functionApps$.next(e);
			this.isLoaded$.next(true);
		});
	}

	updateFunctionAppLinkConfiguration() {
		let dataArray: number[] = [];

		var childData = this.sourceChild.getFunctionAppLinkConfig();

		//Send the function app id's which we want to have a custom version of, everything else will be discarded
		if (childData != null) {
			dataArray.push(childData);
		}
		var targetData = this.targetChild.getFunctionAppLinkConfig();
		if (targetData != null) {
			dataArray.push(targetData);
		}
		var globalData = this.globalChild.getFunctionAppLinkConfig();
		if (globalData != null) {
			dataArray.push(globalData);
		}

		var entryPointData = this.entrypointChild.getFunctionAppLinkConfig();
		if (entryPointData != null) {
			dataArray.push(entryPointData);
		}

		const functionAppsLinkCreateDto: functionAppsLinkCreateDto = {
			functionAppsIds: dataArray,
			linkId: this.link.id,
		};

		this.functionAppsLinkService.updateLinkCustomAppConfig(functionAppsLinkCreateDto).subscribe(() => {
			Swal.fire({
				icon: 'success',
				title: 'Link function app configuration saved',
				showConfirmButton: false,
				timer: 1500,
			});
		});
	}
}

export class FunctionAppCustomConfiguration {
	sourceFunctionApp: functionApps;
	targetFunctionApp: functionApps;
	globalFunctionApp: functionApps;
	entryPointFunctionApp: functionApps;
	sourceFunctionAppList: functionApps[];
	targetFunctionAppList: functionApps[];
	globalFunctionAppList: functionApps[];
	entryPointFunctionAppList: functionApps[];
}
